import {useState, useCallback, useEffect} from 'react';
import {message} from 'antd';
import {isEmpty} from 'underscore';
import moment from 'moment';
import {MarkerItem} from "../../../../types/home";
import useCartStore from "../../../../store/cartStore";
import useAuthStore from "../../../../store/authStore";
import useHomeStore from "../../../../store/homeStore";
import {DownloadInfoItem} from "../../../../types/analytics.d";
import {TFormat} from "../../../../utils/StaticVariable";
import {dateRangeLimit} from "../../../../utils/userGroupValidation";
import {getPointNameCartError} from "../component/Modal/Error/PointNameInCartError";
import useAddShoppingCartItems from "../../../../queries/useAddToCartMutation";
import {PayloadShoppingItems} from "../../../../types/shopping";
import {getCategoryFromRadioKey} from "../../../../utils/getCategoryDataset";
import useGetShoppingCartQuery from "../../../../queries/useGetShoppingCartQuery";

export const useCartManagement = (
  activePoint: MarkerItem | null | undefined,
  setDatasetOfflineText: (item: string) => void,
  setDatasetIsOfflineWarningVisible: (item: boolean) => void,
) => {
  const shoppingItems = useCartStore(state => state.shoppingItems);
  const setShoppingItems = useCartStore(state => state.setShoppingItems);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setShowLogin = useHomeStore(state => state.setShowLogin);

  const addItemToCartMutation = useAddShoppingCartItems();
  const shoppingCart = useGetShoppingCartQuery()

  const [loadingCart, setLoadingCart] = useState<boolean>(false);
  const doAddProductToCart = useCallback(async (
    item: DownloadInfoItem | null,
    pointName: string,
    category: string,
    coordinates: number[]
  ): Promise<boolean> => {
    if (!item || isEmpty(item)) return false;

    const startDate = moment(item.dateRange?.[0]).format(TFormat);
    const endDate = moment(item.dateRange?.[1]).format(TFormat);

    const itemToAdd: PayloadShoppingItems[] = [{
      location: coordinates.join(', '),
      datasetName: item.datasetId,
      fileFormat: 'CSV',
      pointName,
      endDate,
      startDate,
      category
    }]

    try {
      await addItemToCartMutation.mutateAsync(itemToAdd);
      return true;
    } catch (error: any) {
      // Could be "Error: This point name has already been used."
      // When logging in after point creation, point names of current session might
      // conflict with point names in shopping cart of previous sessions
      const errorMessage = error.message || 'Failed to add the item to shopping cart';

      if (errorMessage.includes('offline')) {
        setDatasetOfflineText(errorMessage)
        setDatasetIsOfflineWarningVisible(true);

        return false
      }

      errorMessage === 'Error: This point name has already been used.'
        ? message.error(getPointNameCartError(pointName))
        : message.warn(errorMessage);

      return false;
    }
  }, [addItemToCartMutation]);

  useEffect(() => {
    const data = shoppingCart.data;

    if (data?.shoppingItems) {
      setShoppingItems(data?.shoppingItems);
    }
  }, [shoppingCart.data]);

  const addAllProductToCart = useCallback(async (
    items: PayloadShoppingItems[],
    shoppingItems: Record<string, any>
  ) => {
    setLoadingCart(true)
    const payloadItems: PayloadShoppingItems[] = [];

    for (const item of items) {
      const {
        pointName,
        category,
        location,
        startDate,
        endDate,
        datasetName,
        fileFormat,
      } = item;

      const pointWithSameNameIsInCard = pointName in shoppingItems;

      if (pointWithSameNameIsInCard) {
        const itemsPerPoint = shoppingItems[pointName];

        if (itemsPerPoint[category] !== undefined) continue;
      }

      const cartItem: PayloadShoppingItems = {
        pointName: pointName,
        category: category,
        datasetName: datasetName,
        startDate: startDate || '',
        endDate: endDate || '',
        location: location,
        fileFormat: fileFormat,
      };

      payloadItems.push(cartItem);
    }

    if (payloadItems.length === 0) {
      setLoadingCart(false);
      return;
    }

    try {
      addItemToCartMutation.mutate(payloadItems, {
        onSuccess: () => {
          message.success(`Success adding item to cart`);
          setLoadingCart(false)
        },
        onError: (error) => {
          console.error(`Error adding items to cart`, error);
          message.error(`Failed to add items to cart: ${error.message}`);
          setLoadingCart(false)
        }
      });
    } catch (error) {
      console.error(`Error while processing items for cart`, error);
      setLoadingCart(false);
    }
  }, [addItemToCartMutation, dateRangeLimit, getPointNameCartError, setLoadingCart]);

  const handleAllToCart = useCallback(async () => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    if (activePoint?.label === '') {
      message.warn('Please give the active point a name');
      return;
    }
    const allActivePointItems = activePoint?.downloadPage.map((item) => {
      return {
        pointName: activePoint?.label,
        category: getCategoryFromRadioKey(item.radioKey),
        datasetName: item.datasetId,
        startDate: item.dateRange?.[0].format(TFormat),
        endDate: item.dateRange?.[1].format(TFormat),
        location: activePoint?.coordinates.join(','),
        fileFormat: 'CSV',
      }
    }) ?? []
    if (allActivePointItems.length > 0) {
      await addAllProductToCart(allActivePointItems, shoppingItems)
    }

  }, [isAuthenticated, shoppingItems, activePoint?.downloadPage]);

  return {
    loadingCart,
    handleAllToCart,
    doAddProductToCart,
    getPointNameCartError
  };
};
